// AI_GOOD: Custom hooks extracted for better state management organization
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { debounce } from "lodash";
import { useWebSocket } from "@/hooks/useWebSocket";
import { logger } from "@/utils/logger";
import {
  MissingArticleItemSchema,
  UploadedFile,
  UserStatus,
  MissingArticleTitleOverridePayload,
  MissingArticleTitleRevertRequest,
} from "@/types/missing_articles";
import {
  ALL_POSSIBLE_LANGUAGES,
  TranslationState,
  LanguageToTranslate,
  ANIMATION_CONFIG,
} from "@/pages/MissingArticle/types";

// AI_GOOD: Article details management hook
export const useArticleDetails = (
  itemId: string | undefined,
  apiBaseUrl: string,
  authToken: string,
) => {
  const [articleDetails, setArticleDetails] =
    useState<MissingArticleItemSchema | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // AI_GOOD: fetchArticleDetailsData preserved exactly as in original
  const fetchArticleDetailsData = useCallback(async () => {
    if (!itemId) {
      setError("Missing Article ID not found in URL.");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get<MissingArticleItemSchema>(
        `${apiBaseUrl}/missing-articles/item/${itemId}`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const fetchedData = response.data;
      setArticleDetails(fetchedData);
      setError(null);
    } catch (err) {
      console.error(`Error fetching details for article ${itemId}:`, err);
      let errorMsg = `Failed to load details for Missing Article ${itemId}.`;
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = `API Error: ${err.response.data?.detail || err.message}`;
      } else if (err instanceof Error) {
        errorMsg = `Error: ${err.message}`;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [itemId, apiBaseUrl, authToken]);

  return {
    articleDetails,
    setArticleDetails,
    loading,
    setLoading,
    error,
    setError,
    fetchArticleDetailsData,
  };
};

// AI_GOOD: Title editing functionality hook
export const useTitleEditing = (
  articleDetails: MissingArticleItemSchema | null,
  apiBaseUrl: string,
  authToken: string,
  fetchArticleDetailsData: () => Promise<void>,
) => {
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [renderEditor, setRenderEditor] = useState<boolean>(false);
  const [animateEditorVisible, setAnimateEditorVisible] =
    useState<boolean>(false);

  // AI_GOOD: Initialize titleOverride when articleDetails changes
  useEffect(() => {
    setTitleOverride(articleDetails?.title_override || null);
  }, [articleDetails]);

  // AI_GOOD: Animation effect preserved exactly as in original
  useEffect(() => {
    if (isEditing) {
      setRenderEditor(true);
      const timer = setTimeout(() => {
        setAnimateEditorVisible(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setAnimateEditorVisible(false);
      const timer = setTimeout(() => {
        setRenderEditor(false);
      }, ANIMATION_CONFIG.EXIT_ANIMATION_TOTAL_DURATION);
      return () => clearTimeout(timer);
    }
  }, [isEditing]);

  // AI_GOOD: Title override handler preserved exactly
  const handleTitleOverride = async (
    payload: MissingArticleTitleOverridePayload,
  ) => {
    try {
      await axios.post<MissingArticleTitleOverridePayload>(
        `${apiBaseUrl}/missing-articles/title-override`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      await fetchArticleDetailsData();
      setTitleOverride(payload.title_override);
      setIsEditing(false);
    } catch (err) {
      logger.error("Error overriding missing article title", {
        error: err instanceof Error ? err.message : String(err),
        url: `${apiBaseUrl}/missing-articles/title-override`,
        payload,
      });
      throw err;
    }
  };

  // AI_GOOD: Title revert handler preserved exactly
  const handleTitleRevert = async (
    payload: MissingArticleTitleRevertRequest,
  ) => {
    try {
      await axios.post<MissingArticleTitleRevertRequest>(
        `${apiBaseUrl}/missing-articles/title-revert`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      await fetchArticleDetailsData();
      setTitleOverride(null);
      setIsEditing(false);
    } catch (err) {
      console.error("Error reverting title:", err);
      throw err;
    }
  };

  return {
    isEditingTitle,
    setIsEditingTitle,
    titleOverride,
    setTitleOverride,
    isEditing,
    setIsEditing,
    renderEditor,
    animateEditorVisible,
    handleTitleOverride,
    handleTitleRevert,
  };
};

// AI_GOOD: Status management hook
export const useStatusManagement = (
  articleDetails: MissingArticleItemSchema | null,
  itemId: string | undefined,
  apiBaseUrl: string,
  authToken: string,
  fetchArticleDetailsData: () => Promise<void>,
) => {
  const [isEditingStatus, setIsEditingStatus] = useState<boolean>(false);
  const [editableUserStatus, setEditableUserStatus] = useState<UserStatus | "">(
    "",
  );
  const [editableUserNotes, setEditableUserNotes] = useState<string>("");
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(
    null,
  );
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

  // AI_GOOD: Initialize status and notes when articleDetails changes
  useEffect(() => {
    if (articleDetails) {
      setEditableUserStatus(articleDetails.user_status || "Open");
      setEditableUserNotes(articleDetails.user_notes || "");
    }
  }, [articleDetails]);

  // AI_GOOD: Status update handler preserved exactly
  const handleStatusUpdate = async () => {
    if (!itemId || editableUserStatus === "") return;
    setIsUpdatingStatus(true);
    setStatusUpdateError(null);
    try {
      const payload = {
        user_status: editableUserStatus,
        user_notes: editableUserNotes,
      };
      const response = await axios.put<MissingArticleItemSchema>(
        `${apiBaseUrl}/missing-articles/item/${itemId}/status`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      // AI_GOOD: Update state from response and close editing mode
      setEditableUserStatus(response.data.user_status);
      setEditableUserNotes(response.data.user_notes || "");
      setIsEditingStatus(false);
      await fetchArticleDetailsData();
    } catch (err) {
      console.error("Error updating status:", err);
      let errorMsg = "Failed to update status.";
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = `API Error: ${err.response.data?.detail || err.message}`;
      } else if (err instanceof Error) {
        errorMsg = `Error: ${err.message}`;
      }
      setStatusUpdateError(errorMsg);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return {
    isEditingStatus,
    setIsEditingStatus,
    editableUserStatus,
    setEditableUserStatus,
    editableUserNotes,
    setEditableUserNotes,
    statusUpdateError,
    setStatusUpdateError,
    isUpdatingStatus,
    handleStatusUpdate,
  };
};

// AI_GOOD: Display settings hook for version and language selection
export const useDisplaySettings = (
  articleDetails: MissingArticleItemSchema | null,
  activeTab: string,
) => {
  const [selectedDisplayLanguage, setSelectedDisplayLanguage] =
    useState<string>(ALL_POSSIBLE_LANGUAGES[0]);
  const [selectedDisplayVersion, setSelectedDisplayVersion] = useState<
    number | null
  >(null);

  // AI_GOOD: Memoized selectors preserved exactly as in original
  const allUniqueVersionsSorted = React.useMemo(() => {
    if (!articleDetails?.generated_content?.versions) {
      return [];
    }
    const uniqueVersions = Array.from(
      new Set(articleDetails.generated_content.versions.map((v) => v.version)),
    );
    return uniqueVersions.sort((a, b) => b - a); // Sort descending (latest first)
  }, [articleDetails?.generated_content?.versions]);

  const isLanguageEnabledForVersion = useCallback(
    (lang: string, version: number | null): boolean => {
      if (version === null || !articleDetails?.generated_content?.versions) {
        return false;
      }
      const versionData = articleDetails.generated_content.versions.find(
        (v) => v.version === version,
      );
      return !!versionData?.languages[lang]?.content;
    },
    [articleDetails?.generated_content?.versions],
  );

  // AI_GOOD: Version selection effect preserved exactly
  useEffect(() => {
    if (activeTab === "generated") {
      if (allUniqueVersionsSorted.length > 0) {
        if (
          selectedDisplayVersion === null ||
          !allUniqueVersionsSorted.includes(selectedDisplayVersion)
        ) {
          setSelectedDisplayVersion(allUniqueVersionsSorted[0]);
        }
      } else {
        setSelectedDisplayVersion(null);
        setSelectedDisplayLanguage(ALL_POSSIBLE_LANGUAGES[0]);
      }
    }
  }, [
    activeTab,
    articleDetails?.generated_content?.versions,
    allUniqueVersionsSorted,
    selectedDisplayVersion,
  ]);

  // AI_GOOD: Language selection effect preserved exactly
  useEffect(() => {
    if (activeTab === "generated" && selectedDisplayVersion !== null) {
      if (
        !isLanguageEnabledForVersion(
          selectedDisplayLanguage,
          selectedDisplayVersion,
        )
      ) {
        const firstAvailableLangForVersion = ALL_POSSIBLE_LANGUAGES.find(
          (lang) => isLanguageEnabledForVersion(lang, selectedDisplayVersion),
        );

        if (firstAvailableLangForVersion) {
          setSelectedDisplayLanguage(firstAvailableLangForVersion);
        } else {
          setSelectedDisplayLanguage(ALL_POSSIBLE_LANGUAGES[0]);
        }
      }
    }
  }, [
    activeTab,
    selectedDisplayVersion,
    selectedDisplayLanguage,
    isLanguageEnabledForVersion,
    articleDetails?.generated_content?.versions,
  ]);

  const currentDisplayVersionDetails =
    articleDetails?.generated_content?.versions.find(
      (v) => v.version === selectedDisplayVersion,
    );

  const currentDisplayDoc =
    currentDisplayVersionDetails?.languages[selectedDisplayLanguage];

  return {
    selectedDisplayLanguage,
    setSelectedDisplayLanguage,
    selectedDisplayVersion,
    setSelectedDisplayVersion,
    allUniqueVersionsSorted,
    isLanguageEnabledForVersion,
    currentDisplayVersionDetails,
    currentDisplayDoc,
  };
};

// AI_GOOD: Generation modal state hook
export const useGenerationModal = () => {
  const [isGenerateModalOpen, setIsGenerateModalOpen] =
    useState<boolean>(false);
  const [selectedGenLanguage, setSelectedGenLanguage] = useState<string>("sv");
  const [userPromptAddition, setUserPromptAddition] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [streamingContent, setStreamingContent] = useState<string>("");
  const [streamingThoughts, setStreamingThoughts] = useState<string>("");
  const [enableUrlContext, setEnableUrlContext] = useState<boolean>(false);
  const [enableGoogleSearch, setEnableGoogleSearch] = useState<boolean>(false);
  const [isStreamingToArticleArea, setIsStreamingToArticleArea] =
    useState<boolean>(false);
  const [isThoughtsAccordionOpen, setIsThoughtsAccordionOpen] =
    useState<boolean>(true);
  const [isThinkingComplete, setIsThinkingComplete] = useState<boolean>(false);
  const [generationUserMessage, setGenerationUserMessage] = useState<
    string | null
  >(null);
  const [generationSuccessMessage, setGenerationSuccessMessage] = useState<
    string | null
  >(null);
  const [generationWarningMessage, setGenerationWarningMessage] = useState<
    string | null
  >(null);

  // AI_GOOD: Add thinking progress state for WebSocket updates
  const [currentThinkingSection, setCurrentThinkingSection] = useState<
    string | null
  >(null);

  const userPromptRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (generationSuccessMessage) {
      const timer = setTimeout(() => {
        setGenerationSuccessMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [generationSuccessMessage]);

  // AI_GOOD: Prompt synchronization preserved exactly
  const syncUserPromptFromRef = useCallback(() => {
    if (userPromptRef.current) {
      setUserPromptAddition(userPromptRef.current.value);
    }
  }, []);

  const getCurrentUserPrompt = useCallback(() => {
    syncUserPromptFromRef();
    return userPromptAddition;
  }, [userPromptAddition, syncUserPromptFromRef]);

  // AI_GOOD: File handling preserved exactly
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles: UploadedFile[] = Array.from(files).map((file) => ({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
      }));
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const removeUploadedFile = (fileIdToRemove: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileIdToRemove));
  }; // AI_GOOD: Debounced sync handler - use useMemo to create debounced function
  const debouncedSyncHandler = React.useMemo(
    () =>
      debounce(() => {
        syncUserPromptFromRef();
      }, 1000),
    [syncUserPromptFromRef],
  );

  return {
    isGenerateModalOpen,
    setIsGenerateModalOpen,
    selectedGenLanguage,
    setSelectedGenLanguage,
    userPromptAddition,
    setUserPromptAddition,
    userPromptRef,
    syncUserPromptFromRef,
    getCurrentUserPrompt,
    uploadedFiles,
    setUploadedFiles,
    isGenerating,
    setIsGenerating,
    generationError,
    setGenerationError,
    streamingContent,
    setStreamingContent,
    streamingThoughts,
    setStreamingThoughts,
    enableUrlContext,
    setEnableUrlContext,
    enableGoogleSearch,
    setEnableGoogleSearch,
    isStreamingToArticleArea,
    setIsStreamingToArticleArea,
    isThoughtsAccordionOpen,
    setIsThoughtsAccordionOpen,
    isThinkingComplete,
    setIsThinkingComplete,
    generationUserMessage,
    setGenerationUserMessage,
    generationSuccessMessage,
    setGenerationSuccessMessage,
    generationWarningMessage,
    setGenerationWarningMessage,
    currentThinkingSection,
    setCurrentThinkingSection,
    fileInputRef,
    handleFileChange,
    removeUploadedFile,
    debouncedSyncHandler,
  };
};

// AI_GOOD: Translation state hook
export const useTranslation = () => {
  const [translatingState, setTranslatingState] = useState<
    Record<string, TranslationState>
  >({});
  const [pendingTranslations, setPendingTranslations] = useState<
    Record<string, boolean>
  >({});
  const [isTranslateConfirmModalOpen, setIsTranslateConfirmModalOpen] =
    useState<boolean>(false);
  const [languageToTranslate, setLanguageToTranslate] =
    useState<LanguageToTranslate | null>(null);

  return {
    translatingState,
    setTranslatingState,
    pendingTranslations,
    setPendingTranslations,
    isTranslateConfirmModalOpen,
    setIsTranslateConfirmModalOpen,
    languageToTranslate,
    setLanguageToTranslate,
  };
};

// AI_GOOD: Modal management hook
export const useModalManagement = () => {
  const [modalImageUrl, setModalImageUrl] = useState<string | null>(null);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState<boolean>(false);
  const [promptModalContent, setPromptModalContent] = useState<string>("");

  const imageModalRef = useRef<HTMLDialogElement>(null);
  const descriptionContainerRef = useRef<HTMLDivElement>(null);

  return {
    modalImageUrl,
    setModalImageUrl,
    isPromptModalOpen,
    setIsPromptModalOpen,
    promptModalContent,
    setPromptModalContent,
    imageModalRef,
    descriptionContainerRef,
  };
};

// AI_GOOD: WebSocket message processing hook - now focused on status updates only
export const useWebSocketMessages = (
  itemId: string | undefined,
  fetchArticleDetailsData: () => Promise<void>,
  setStreamingContent: (value: React.SetStateAction<string>) => void,
  setStreamingThoughts: (value: React.SetStateAction<string>) => void,
  setGenerationUserMessage: (
    value: React.SetStateAction<string | null>,
  ) => void,
  setGenerationError: (value: React.SetStateAction<string | null>) => void,
  setGenerationSuccessMessage: (
    value: React.SetStateAction<string | null>,
  ) => void,
  setIsGenerating: (value: React.SetStateAction<boolean>) => void,
  setIsStreamingToArticleArea: (value: React.SetStateAction<boolean>) => void,
  setIsThoughtsAccordionOpen: (value: React.SetStateAction<boolean>) => void,
  setIsThinkingComplete: (value: React.SetStateAction<boolean>) => void,
  setActiveTab: (tab: "details" | "generated") => void,
  setSelectedDisplayVersion: (version: number) => void,
  setSelectedDisplayLanguage: (lang: string) => void,
  setCurrentThinkingSection: (
    value: React.SetStateAction<string | null>,
  ) => void,
) => {
  const { lastMessage } = useWebSocket();

  // AI_GOOD: WebSocket message processing focused on status updates only
  useEffect(() => {
    if (lastMessage && lastMessage.data && itemId) {
      try {
        const message = JSON.parse(lastMessage.data as string);
        // AI_GOOD: Ensure message is for the current item
        if (message.item_id !== itemId) return;

        logger.info("WebSocket message received for this item:", message);

        if (message.type === "generation_progress") {
          const { status, message: progressMessage, version, lang } = message;

          // AI_GOOD: Handle thinking progress messages
          if (status === "thinking") {
            setCurrentThinkingSection(progressMessage);
            setGenerationUserMessage(`Thinking: ${progressMessage}`);
            logger.info(`Thinking progress: ${progressMessage}`);
          }
          // AI_FIXED: Handle content generation progress messages - DO NOT update user message here
          else if (status === "generating_content") {
            // Content streaming is handled by the HTTP response stream.
            // This WS message might indicate start/end of content phase,
            // but not the content chunks themselves.
            // We should not set the streamed content to generationUserMessage.
            // We could update a different status indicator if needed,
            // but based on the request, generationUserMessage should be clean.
            logger.info(`Content generation status: ${progressMessage}`);
          }

          // AI_GOOD: Process status updates only
          else if (status === "error") {
            setGenerationError(progressMessage);
            setGenerationSuccessMessage(null);
            setGenerationUserMessage(null);
            setCurrentThinkingSection(null);
            setIsGenerating(false);
            setIsStreamingToArticleArea(false);
          } else if (status === "completed") {
            setGenerationSuccessMessage(progressMessage);
            setGenerationError(null);
            setGenerationUserMessage(null);
            setCurrentThinkingSection(null);
            setIsGenerating(false);
            setIsStreamingToArticleArea(false);

            // Auto-close the thoughts accordion when generation completes
            setIsThinkingComplete(true);

            // Only fetch new article details when generation is truly complete
            if (lang && typeof version === "number") {
              fetchArticleDetailsData().then(() => {
                setActiveTab("generated");
                setSelectedDisplayVersion(version);
                if (lang) setSelectedDisplayLanguage(lang);
              });
            }
          } else if (status === "started") {
            setGenerationUserMessage(progressMessage);
            setGenerationError(null);
            setGenerationSuccessMessage(null);
            setCurrentThinkingSection(null);
            setIsGenerating(true);
            // Note: We don't set setIsStreamingToArticleArea here since HTTP stream controls that
          } else {
            // Handle other general status messages (e.g., "in_progress", other steps)
            // Only update user message if it's not one of the specific handling cases above
            // and seems like a general status update.
            if (
              status !== "thinking" &&
              status !== "generating_content" &&
              status !== "completed" &&
              status !== "error" &&
              status !== "started"
            ) {
              setGenerationUserMessage(progressMessage);
              logger.info(`Other progress message: ${progressMessage}`);
            }

            if (status === "in_progress") {
              setIsGenerating(true);
            }
          }
        } else if (message.type === "translation_progress") {
          // AI_GOOD: Existing translation progress handling preserved
          // Implementation would go here
        }
      } catch (e) {
        console.error("Error processing WebSocket message:", e);
      }
    }
  }, [
    lastMessage,
    itemId,
    fetchArticleDetailsData,
    setStreamingContent,
    setStreamingThoughts,
    setGenerationUserMessage,
    setGenerationError,
    setGenerationSuccessMessage,
    setIsGenerating,
    setIsStreamingToArticleArea,
    setIsThoughtsAccordionOpen,
    setIsThinkingComplete,
    setActiveTab,
    setSelectedDisplayVersion,
    setSelectedDisplayLanguage,
    setCurrentThinkingSection,
  ]);
};
